import React, { useState, useEffect } from 'react';
import { getDrawingsByReference } from '../../services/analyses';
import './ReferenceHistory.css';

/** La referencia de la pieza con cuántas veces se ha fabricado. Al pulsar, los pedidos.
 *
 *  Es el dato más fiable que tenemos en una búsqueda: medido, 17 de 20 parejas de planos que
 *  comparten referencia son la misma pieza, mientras que el parecido visual no ordena.
 *
 *  `count` es opcional: cuando se pintan muchas tarjetas a la vez lo manda el backend ya contado
 *  (una sola consulta para toda la lista). Si no viene, lo pregunta el propio componente, que es
 *  lo que interesa cuando solo hay una referencia en pantalla.
 */
const ReferenceHistory = ({ reference, count }) => {
  const [abierto, setAbierto] = useState(false);
  const [pedidos, setPedidos] = useState(null);
  const [veces, setVeces] = useState(count ?? null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setVeces(count ?? null);
    setPedidos(null);
    setAbierto(false);
    setError(false);
    if (count !== undefined && count !== null) return undefined;

    let vivo = true;
    getDrawingsByReference(reference)
      .then((data) => {
        if (!vivo) return;
        setVeces(data.count || 0);
        setPedidos(data.drawings || []);
      })
      .catch(() => { if (vivo) setVeces(0); });
    return () => { vivo = false; };
  }, [reference, count]);

  const alternar = async () => {
    const abrir = !abierto;
    setAbierto(abrir);
    if (!abrir || pedidos) return;
    try {
      const data = await getDrawingsByReference(reference);
      setPedidos(data.drawings || []);
    } catch (e) {
      setError(true);
    }
  };

  // Con una sola aparición no hay historial que enseñar (el único pedido sería este mismo):
  // la referencia se queda como dato, igual que antes
  if (!veces || veces < 2) {
    return <span className="reference-history-plana" title="Referencia de la pieza">{reference}</span>;
  }
  return (
    <span className="reference-history">
      <button
        className={`reference-history-btn${abierto ? ' abierto' : ''}`}
        onClick={alternar}
        title="Ver los pedidos de esta misma pieza"
      >
        {reference}
        <span className="reference-history-veces">
          {veces} {veces === 1 ? 'pedido' : 'pedidos'}
        </span>
      </button>
      {abierto && (
        <span className="reference-history-lista">
          {error && <span className="reference-history-vacio">No se ha podido cargar el historial.</span>}
          {!error && !pedidos && <span className="reference-history-vacio">Buscando pedidos…</span>}
          {pedidos?.map((p) => (
            <button
              key={p.analysis_hash}
              className="reference-history-pedido"
              onClick={() => window.open(`/analysis/${p.analysis_hash}`, '_blank')}
              title={`${p.filename} — abrir en una pestaña nueva`}
            >
              <span className="reference-history-num">{p.numero_pedido || p.filename}</span>
              <span className="reference-history-fecha">{(p.fecha_documento || p.analyzed_at || '').slice(0, 10)}</span>
            </button>
          ))}
        </span>
      )}
    </span>
  );
};

export default ReferenceHistory;
